import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, ArrowRight, Lightbulb, Mail, Feather } from "lucide-react";
import { API } from "../App";

const SERIF = { fontFamily: "'Playfair Display', serif" };
const MAROON = "#7A2E2E";
const SAGE = "#2F5A2E";

// Beyond the Book feature — set articleSlug to "" (or null) to hide the CTA
const BEYOND_FEATURE = {
  articleSlug: "3-books-that-will-change-the-way-you-think",
  ctaLabel: "Explore the Thinking",
};

const useArticleExists = (slug) => {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (!slug) { setOk(false); return; }
    let live = true;
    axios.get(`${API}/blog/${slug}`).then(() => live && setOk(true)).catch(() => live && setOk(false));
    return () => { live = false; };
  }, [slug]);
  return ok;
};

const CardLabel = ({ icon: Icon, color, children }) => (
  <div className="flex items-center gap-2.5 mb-5">
    <Icon className="w-[18px] h-[18px]" style={{ color }} strokeWidth={1.6} />
    <p className="uppercase tracking-[0.2em] text-[11px] md:text-[12px] font-bold" style={{ color }}>{children}</p>
  </div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-[26px] lg:text-[25px] xl:text-[28px] 2xl:text-[30px] font-bold leading-[1.15] mb-4" style={{ ...SERIF, color: "#2A1712" }}>
    {children}
  </h3>
);

const CardShell = ({ bg, img, imgPos, fade, testid, className = "", children }) => (
  <div className={`relative rounded-2xl overflow-hidden flex ${className}`} style={{ background: bg, minHeight: 420, boxShadow: "0 1px 2px rgba(60,30,20,0.06), 0 10px 30px -12px rgba(60,30,20,0.18)" }} data-testid={testid}>
    <img src={img} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: imgPos }} />
    <div className="absolute inset-0" style={{ background: fade }} />
    <div className="relative z-10 flex flex-col w-full p-6 md:p-7 2xl:p-8">{children}</div>
  </div>
);

const Cover = ({ to, src, alt, testid }) => (
  <Link to={to} className="block transition-transform duration-300 hover:-translate-y-1.5" data-testid={testid}>
    <img src={src} alt={alt} className="h-[112px] sm:h-[150px] lg:h-[118px] xl:h-[140px] w-auto object-contain rounded-[3px]" style={{ boxShadow: "0 12px 22px -8px rgba(40,20,10,0.45)" }} />
  </Link>
);

const BigIdeaCard = () => (
  <CardShell bg="#FBF0E6" img="/readers-corner/focus-bg.jpg" imgPos="right center" testid="readers-card-big-idea"
    fade="linear-gradient(90deg, #FBF0E6 0%, rgba(251,240,230,0.96) 42%, rgba(251,240,230,0.35) 68%, rgba(251,240,230,0) 100%)">
    <CardLabel icon={BookOpen} color={MAROON}>One Book, One Big Idea</CardLabel>
    <div className="flex gap-4 flex-1">
      <div className="flex flex-col flex-1 min-w-0">
        <CardTitle>Are You Paying Attention to What Truly Matters?</CardTitle>
        <p className="text-[15px] leading-[1.6] mb-7" style={{ color: "#3F342C" }}>
          In a world full of distractions, Daniel Goleman reminds us that what we choose to focus on shapes how we think, connect and grow.
        </p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-auto">
          <a href="/reflection/focus-big-idea" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 h-11 rounded-lg text-[13px] font-semibold transition-[opacity,box-shadow] hover:opacity-90 hover:shadow-md" style={{ background: "#6B1F1A", color: "#FFF" }} data-testid="big-idea-explore-cta">
            Explore the Big Idea <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </a>
          <Link to="/book/book_0032" className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-opacity hover:opacity-70" style={{ color: MAROON }} data-testid="big-idea-library-link">
            View in Library <ArrowRight className="w-3 h-3" strokeWidth={2} />
          </Link>
        </div>
      </div>
      <div className="flex items-end justify-center flex-shrink-0 w-[100px] sm:w-[130px] lg:w-[112px] xl:w-[132px] pb-3">
        <Link to="/book/book_0032" className="block transition-transform duration-300 hover:-translate-y-1.5" data-testid="big-idea-cover">
          <img src="/covers/focus-the-hidden-driver-of-excellence.jpg" alt="Focus: The Hidden Driver of Excellence" className="w-full h-auto rounded-[3px]" style={{ boxShadow: "0 18px 30px -10px rgba(40,20,10,0.5)" }} />
        </Link>
      </div>
    </div>
  </CardShell>
);

const BeyondCard = () => {
  const { articleSlug, ctaLabel } = BEYOND_FEATURE;
  const showCta = useArticleExists(articleSlug);
  return (
  <CardShell bg="#EEF0E3" img="/readers-corner/thinking-bg.jpg" imgPos="right top" testid="readers-card-beyond"
    fade="linear-gradient(90deg, #EEF0E3 0%, rgba(238,240,227,0.95) 45%, rgba(238,240,227,0.45) 75%, rgba(238,240,227,0.15) 100%)">
    <CardLabel icon={Lightbulb} color={SAGE}>Beyond the Book</CardLabel>
    <div className="max-w-[420px] -mx-3 px-3 pt-3 pb-1 mb-4 rounded-xl" style={{ background: "rgba(226,234,214,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} data-testid="beyond-text-panel">
      <CardTitle>Why Do Intelligent People Make Poor Decisions?</CardTitle>
      <p className="text-[15px] leading-[1.6] mb-2" style={{ color: "#2E3326" }}>
        Even intelligent minds fall into thinking traps. These three books explore how biases, assumptions and everyday thought patterns shape our decisions — and how we can learn to think more clearly.
      </p>
    </div>
    <div className="flex flex-wrap-reverse items-end justify-between gap-4 mt-auto">
      {showCta && (
      <Link to={`/blog/${articleSlug}`} className="inline-flex items-center gap-2 px-5 h-11 rounded-lg text-[13px] font-semibold transition-[opacity,box-shadow] hover:opacity-90 hover:shadow-md" style={{ background: SAGE, color: "#FFF" }} data-testid="beyond-explore-cta">
        {ctaLabel} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
      </Link>
      )}
      <div className="flex items-end gap-2.5 sm:gap-3 ml-auto">
        <Cover to="/book/book_8edb9f2edb63" src="/covers/thinking-fast-and-slow.jpg" alt="Thinking, Fast and Slow" testid="beyond-cover-thinking-fast" />
        <Cover to="/book/book_0044" src="/covers/the-art-of-thinking-clearly.jpg" alt="The Art of Thinking Clearly" testid="beyond-cover-art-thinking" />
        <Cover to="/book/book_2d8e0a713091" src="/covers/think-straight.jpg" alt="Think Straight" testid="beyond-cover-think-straight" />
      </div>
    </div>
  </CardShell>
  );
};

const Benefit = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-5 h-5 flex-shrink-0" style={{ color: "#B5523B" }} strokeWidth={1.4} />
    <span className="text-[11.5px] leading-tight" style={{ color: "#5A4A3E" }}>{children}</span>
  </div>
);

const ConnectCard = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/subscribe`, { email });
      if (res.data.already_subscribed) toast.info("You're already subscribed!");
      else toast.success("Thank you! You'll hear from us soon.");
      setEmail("");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSending(false); }
  };
  return (
    <CardShell bg="#FCEDE0" img="/readers-corner/connect-bg.jpg" imgPos="right center" testid="email-signup-section" className="md:col-span-2 lg:col-span-1"
      fade="linear-gradient(90deg, #FCEDE0 0%, rgba(252,237,224,0.97) 55%, rgba(252,237,224,0.55) 78%, rgba(252,237,224,0.1) 100%)">
      <CardLabel icon={Mail} color="#B5352A">Stay Curious, Stay Connected</CardLabel>
      <div className="max-w-[330px]">
        <CardTitle>A Little Reading Inspiration, Delivered to You.</CardTitle>
        <p className="text-[15px] leading-[1.6] mb-6" style={{ color: "#3F342C" }}>
          Discover book ideas, fresh perspectives and thoughtful reflections — straight to your inbox.
        </p>
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2.5 mb-6 max-w-[420px]">
        <input type="email" required placeholder="Your email address" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full sm:flex-1 min-w-0 h-12 flex-shrink-0 px-4 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#D9A441]"
          style={{ background: "#FFF", border: "1px solid rgba(0,0,0,0.06)", color: "var(--text)" }} data-testid="email-signup-input" />
        <button type="submit" disabled={sending} className="h-12 px-6 rounded-lg text-[14px] font-semibold flex items-center justify-center gap-2 whitespace-nowrap transition-[opacity,box-shadow] hover:opacity-90 hover:shadow-md" style={{ background: "#A3291F", color: "#FFF" }} data-testid="email-signup-btn">
          {sending ? "..." : (<>Subscribe <ArrowRight className="w-4 h-4" strokeWidth={2} /></>)}
        </button>
      </form>
      <div className="flex flex-wrap gap-x-4 gap-y-3 mt-auto">
        <Benefit icon={BookOpen}>Curated book<br />recommendations</Benefit>
        <Benefit icon={Lightbulb}>Ideas worth<br />reflecting on</Benefit>
        <Benefit icon={Feather}>New articles<br />and personal reflections</Benefit>
      </div>
    </CardShell>
  );
};

const Sprig = () => (
  <svg className="hidden lg:block absolute -top-10 right-4 w-40 h-40" viewBox="0 0 160 160" fill="none" stroke="#D9A08A" strokeWidth="1.1" aria-hidden="true">
    <path d="M40 150C70 110 100 70 150 15" />
    <path d="M70 108c-18-6-26-22-24-40 16 6 26 22 24 40z" />
    <path d="M92 80c-16-8-22-24-18-40 14 8 22 24 18 40z" />
    <path d="M115 52c-14-8-18-22-14-36 12 8 18 22 14 36z" />
    <path d="M78 98c14-14 32-16 46-10-12 14-30 16-46 10z" />
    <path d="M102 70c12-12 28-14 40-8-10 12-26 14-40 8z" />
  </svg>
);

export const ReadersCorner = () => (
  <section id="community" className="py-16 md:py-20 px-5 md:px-10" style={{ background: "#FDF3EA" }} data-testid="community-section">
    <div className="max-w-[1440px] mx-auto">
      <div className="text-center mb-10 md:mb-12 relative">
        <Sprig />
        <p className="uppercase tracking-[0.3em] text-[13px] md:text-[15px] font-semibold mb-2" style={{ color: "#B5352A" }}>Readers' Corner</p>
        <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-[1.1]" style={{ ...SERIF, color: "#2A1712" }} data-testid="community-title">
          Books. Ideas. Perspectives.
        </h2>
        <p className="text-sm md:text-base leading-relaxed max-w-2xl mx-auto" style={{ color: "#5A4F43" }} data-testid="community-subtitle">
          Explore thoughtful ideas, deeper connections, and stay updated with new reads from The Reading Room.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <BigIdeaCard />
        <BeyondCard />
        <ConnectCard />
      </div>
    </div>
  </section>
);
